// Group Template 3 — "Atelier" — a premium LIGHT, editorial / magazine-style
// group landing page, deliberately distinct from Template 1's glassy aurora look
// and Template 2's dark neon look: warm paper background, thin hairline rules,
// small-caps letter-spaced eyebrows, elegant Playfair Display serif headlines and
// Inter body copy, small section index numbers, and refined quote-block
// testimonials. Burgundy primary accent with gold used sparingly. Same data
// contract and join / pricing / referral functionality as Template 1/2.
import { Fragment, useEffect, useState } from "react";
import {
    Icon, Stars, splitTitle, rgba, shade, useGoogleFonts, orderedSections,
    Reveal, GuaranteeBadge, skillIconD, PolicyLinks, SOCIAL_ICONS, videoEmbedUrl,
} from "../course-templates/shared";
import GroupSwitcher from "../GroupSwitcher";
import InstructorAvatar from "../InstructorAvatar";
import PoweredByFlexiFunnels from "../PoweredByFlexiFunnels";
import { ReferralLink } from "../CommunityViews";

const DEFAULT_SKILLS = [
    "Exclusive trainings and insider knowledge",
    "High-value resources and downloadable guides",
    "Proven frameworks and strategies for real results",
    "Step-by-step learning content you can apply immediately",
    "Group discussions and support",
    "Access to special workshops, masterclasses, and events",
];

const escapeHtml = (s) => String(s || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// Template 3's OWN signature palette — burgundy + gold (editorial). Do NOT fall
// back to the community's primary; unsaved previews should show these.
const SIGNATURE = "#9F1239";
const SIGNATURE2 = "#C99A2E";

const guaranteeDefaultHtml = (days = 30) =>
    `<p>We believe in the value of this group and are confident it will help you achieve your goals. That's why we offer a full ${days}-day risk-free money-back guarantee.</p><p>Explore everything inside, connect with the community, and put what you learn into action. If at any point within the first ${days} days you feel the group isn't right for you or doesn't meet your expectations, simply reach out to us.</p><p>We'll process your refund promptly—no questions asked.</p><p>There's absolutely no risk in getting started today, only the opportunity to learn, grow, and succeed with complete peace of mind.</p>`;

// editorial paper tokens
const PAPER = "#fbfaf7";
const INK = "#1c1917";
const SERIF = "'Playfair Display',serif";
const BODYFONT = "'Inter',sans-serif";

export default function GroupTemplate3(ctx) {
    const {
        community, group, landing, primary,
        media = [], defaultImage,
        accessLabel, isPrivate, pricing, isPaid, amountLabel,
        joinLabel, skillHeading, skillList = [], shortDescription, postCount, courseStat,
        member, embedded = false, referral, token, gid, myGroups = [],
        onJoin, onLogin, onInstructor, onSwitchGroup, onDiscoverGroups,
    } = ctx;

    useGoogleFonts("https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=Inter:wght@400;500;600;700&display=swap");

    const A = (landing.theme?.color1 && String(landing.theme.color1).trim()) || SIGNATURE;
    const B = (landing.theme?.color2 && String(landing.theme.color2).trim()) || SIGNATURE2;
    const AD = shade(A, -0.22);

    const t = splitTitle(landing.header?.headline || group.name);
    const sub = landing.header?.subheadline || shortDescription || "";
    const instructors = Array.isArray(group.instructors) && group.instructors.length
        ? group.instructors
        : (group.instructor ? [group.instructor] : []);
    const instructor = instructors[0] || null;
    const memberCount = group.member_count ?? 0;
    const mediaShow = landing.media_show !== false && media.length > 0;

    const skHeading = (skillHeading || "").trim() || "Skills you will walk away with";
    const skItems = (skillList && skillList.length ? skillList : DEFAULT_SKILLS).filter(Boolean);

    const aboutHtml = (landing.description || "").trim()
        ? landing.description
        : (group.description || "").trim()
            ? `<p>${escapeHtml(group.description)}</p>`
            : "";

    const order = orderedSections(landing.section_order).filter((id) => id !== "include");

    const navLinks = [
        landing.about?.show !== false && aboutHtml && { href: "#at-about", label: "About" },
        landing.skill?.show !== false && skItems.length > 0 && { href: "#at-skill", label: "Skills" },
        landing.instructor?.show !== false && instructor?.name && { href: "#at-instructor", label: "Instructor" },
        landing.testimonials?.show && landing.testimonials.list?.length > 0 && { href: "#at-reviews", label: "Reviews" },
        landing.faq?.show && landing.faq.list?.length > 0 && { href: "#at-faq", label: "FAQ" },
    ].filter(Boolean);

    const social = landing.social || {};
    const socialLinks = Object.entries(SOCIAL_ICONS).filter(([k]) => social[k]);

    const chips = (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px]">
            <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: AD }}>
                <Icon d={isPrivate ? "M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 0h10.5a1.5 1.5 0 011.5 1.5v6a1.5 1.5 0 01-1.5 1.5H6.75a1.5 1.5 0 01-1.5-1.5v-6a1.5 1.5 0 011.5-1.5z" : "M12 21a9 9 0 100-18 9 9 0 000 18zm0 0a8.95 8.95 0 003.6-.75M12 3a8.95 8.95 0 00-3.6.75M3.6 9h16.8M3.6 15h16.8"} className="h-3.5 w-3.5" stroke={2.2} />
                {accessLabel}
            </span>
            <span className="h-3 w-px" style={{ background: rgba(INK, 0.16) }} />
            <span className="inline-flex items-center gap-1.5 font-semibold text-stone-500">
                <Icon d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" className="h-3.5 w-3.5" stroke={2} />
                {memberCount} {memberCount === 1 ? "member" : "members"}
            </span>
            <span className="h-3 w-px" style={{ background: rgba(INK, 0.16) }} />
            <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: pricing === "free" ? "#4d7c0f" : AD }}>
                <Icon d={pricing === "free" ? "M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" : pricing === "free_trial" ? "M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" : "M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"} className="h-3.5 w-3.5" stroke={2} />
                {pricing === "free" ? "Free" : pricing === "free_trial" ? `Free trial${amountLabel ? ` · ${amountLabel}` : ""}` : `Paid${amountLabel ? ` · ${amountLabel}` : ""}`}
            </span>
            {instructors.length > 0 && (
                <>
                    <span className="h-3 w-px" style={{ background: rgba(INK, 0.16) }} />
                    {instructors.map((ins) => (
                    <button key={ins.instructor_id} type="button" onClick={() => onInstructor && onInstructor(ins.instructor_id, A, false)} className="inline-flex items-center gap-2 font-semibold text-stone-500 transition hover:text-stone-900">
                        <InstructorAvatar name={ins.name} image={ins.profile_image} primary={A} sizeClass="h-6 w-6" textClass="text-[10px]" /> <span className="underline decoration-dotted decoration-1 underline-offset-[3px]">{ins.name}</span><Icon d="M8.25 4.5l7.5 7.5-7.5 7.5" className="ml-1 h-3 w-3 opacity-70" stroke={2.5} />
                    </button>
                    ))}
                </>
            )}
        </div>
    );

    const JoinButton = ({ full }) => (
        <button onClick={() => onJoin(group)} style={{ background: A, boxShadow: `0 14px 30px -12px ${rgba(A, 0.6)}` }}
            className={`group/join inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-[14px] font-semibold uppercase tracking-[0.12em] text-white transition-all duration-200 hover:-translate-y-0.5 active:scale-[.98] ${full ? "w-full" : ""}`}>
            {!group.joined && <Icon d="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" stroke={2.4} />}
            <span>{joinLabel}</span>
        </button>
    );

    const stats = [["Posts", postCount], ["Members", memberCount], ["Courses", courseStat]];

    // ---- reorderable content blocks ----
    const blocks = {
        about: landing.about?.show !== false && aboutHtml && (
            <Reveal>
                <Sec id="at-about" n={1} eyebrow="Overview" title="About this group" A={A} AD={AD}>
                    <div
                        className="mt-6 max-w-3xl text-[16px] leading-[1.85] text-stone-700 [&_h1]:mb-2 [&_h1]:mt-8 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-stone-900 [&_h1]:[font-family:'Playfair_Display',serif] [&_h2]:mb-2 [&_h2]:mt-6 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-stone-900 [&_h2]:[font-family:'Playfair_Display',serif] [&_h3]:mb-1.5 [&_h3]:mt-5 [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-stone-900 [&_h3]:[font-family:'Playfair_Display',serif] [&_h4]:mb-1 [&_h4]:mt-4 [&_h4]:font-bold [&_h4]:text-stone-900 [&_p]:mb-3 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_strong]:font-bold [&_strong]:text-stone-900 [&_b]:font-bold [&_em]:italic [&_a]:font-semibold [&_a]:text-[color:var(--a)] [&_a]:underline first:[&_h1]:mt-0 first:[&_h2]:mt-0"
                        style={{ "--a": A }}
                        dangerouslySetInnerHTML={{ __html: aboutHtml }}
                    />
                </Sec>
            </Reveal>
        ),
        skill: landing.skill?.show !== false && skItems.length > 0 && (
            <Reveal>
                <Sec id="at-skill" n={2} eyebrow="What you'll learn" title={skHeading} A={A} AD={AD}>
                    <div className="mt-8 grid gap-x-10 gap-y-6 sm:grid-cols-2">
                        {skItems.map((s, i) => (
                            <div key={i} className="flex items-start gap-4 border-t pt-5" style={{ borderColor: rgba(INK, 0.1) }}>
                                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full" style={{ background: rgba(A, 0.08), color: A }}>
                                    <Icon d={skillIconD(landing.skill?.icon)} className="h-4 w-4" stroke={2} />
                                </span>
                                <span className="text-[15.5px] leading-relaxed text-stone-700">{s}</span>
                            </div>
                        ))}
                    </div>
                    {landing.skill?.long_desc && <div className="mt-6 max-w-2xl text-[15.5px] leading-relaxed text-stone-500" dangerouslySetInnerHTML={{ __html: landing.skill.long_desc }} />}
                </Sec>
            </Reveal>
        ),
        instructor: landing.instructor?.show !== false && instructor?.name && (
            <Reveal>
                <div id="at-instructor" className="scroll-mt-24">
                    <Sec n={3} eyebrow="Instructor" title={instructors.length > 1 ? "Meet the instructors" : "Meet the instructor"} A={A} AD={AD}>
                      <div className="space-y-10">
                        {instructors.map((ins) => (
                        <div key={ins.instructor_id} className="mt-8 grid items-start gap-8 sm:grid-cols-[220px_1fr] sm:gap-12">
                            <div className="relative mx-auto sm:mx-0">
                                <span className="absolute -left-2 -top-2 h-full w-full border" style={{ borderColor: rgba(B, 0.55) }} />
                                <div className="relative h-52 w-52 overflow-hidden bg-white sm:h-56 sm:w-56" style={{ boxShadow: `0 24px 50px -24px ${rgba(INK, 0.4)}` }}>
                                    {ins.profile_image
                                        ? <img src={ins.profile_image} alt="" className="h-full w-full object-cover" />
                                        : <span className="flex h-full w-full items-center justify-center text-6xl" style={{ fontFamily: SERIF, color: A, background: rgba(A, 0.08) }}>{ins.name.charAt(0).toUpperCase()}</span>}
                                </div>
                            </div>
                            <div className="text-center sm:pt-4 sm:text-left">
                                <h3 className="text-[2rem] leading-tight text-stone-900 sm:text-[2.5rem]" style={{ fontFamily: SERIF, fontWeight: 700 }}>{ins.name}</h3>
                                <span className="mx-auto mt-4 block h-px w-16 sm:mx-0" style={{ background: A }} />
                                {ins.short_description
                                    ? <p className="mt-6 text-[16px] leading-[1.85] text-stone-600" style={{ fontStyle: "italic" }}>{ins.short_description}</p>
                                    : <p className="mt-6 text-[15px] italic text-stone-400">Meet the person leading this community.</p>}
                            </div>
                        </div>
                        ))}
                      </div>
                    </Sec>
                </div>
            </Reveal>
        ),
        testimonial: landing.testimonials?.show && landing.testimonials.list?.length > 0 && (
            <Reveal>
                <div id="at-reviews" className="scroll-mt-24">
                    <Sec n={4} eyebrow="Testimonials" title={landing.testimonials.heading || "What students are saying"} A={A} AD={AD}>
                        <div className="mt-10 grid gap-x-12 gap-y-10 sm:grid-cols-2">
                            {landing.testimonials.list.map((r, i) => (
                                <figure key={i} className="relative">
                                    <span className="block text-[3.5rem] leading-[0.5] text-stone-300" style={{ fontFamily: SERIF }} aria-hidden>&ldquo;</span>
                                    <blockquote className="mt-4 text-[17px] leading-[1.75] text-stone-800" style={{ fontFamily: SERIF, fontStyle: "italic", fontWeight: 500 }}>{r.quote}</blockquote>
                                    <Stars value={r.rating || 5} className="mt-5 h-4 w-4" color={B} />
                                    <figcaption className="mt-4 flex items-center gap-3">
                                        <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full text-sm font-bold text-white" style={{ background: A }}>{r.avatar ? <img src={r.avatar} alt="" className="h-full w-full object-cover" /> : (r.name || "M").charAt(0).toUpperCase()}</span>
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-bold text-stone-900">{r.name}</p>
                                            {r.role && <p className="truncate text-[13px] text-stone-500">{r.role}</p>}
                                        </div>
                                    </figcaption>
                                </figure>
                            ))}
                        </div>
                    </Sec>
                </div>
            </Reveal>
        ),
        moneyback: landing.guarantee?.show && pricing !== "free" && (
            <Reveal>
                <div className="relative overflow-hidden border p-8 sm:p-12" style={{ background: "#fff", borderColor: rgba(A, 0.22), boxShadow: `0 30px 70px -40px ${rgba(INK, 0.35)}` }}>
                    <span className="pointer-events-none absolute inset-x-0 top-0 h-1" style={{ background: `linear-gradient(90deg, ${A}, ${B})` }} />
                    <div className="grid items-center gap-8 sm:grid-cols-[auto_1fr] sm:gap-12">
                        <div className="mx-auto"><GuaranteeBadge variant={landing.guarantee.badge || 1} days={landing.guarantee.days || 30} accent={A} className="h-32 w-32" /></div>
                        <div className="text-center sm:text-left">
                            <span className="text-[0.7rem] font-bold uppercase tracking-[0.22em]" style={{ color: B }}>Our Promise</span>
                            <h3 className="mt-2 text-[1.9rem] leading-tight text-stone-900 sm:text-[2.3rem]" style={{ fontFamily: SERIF, fontWeight: 700 }}>{landing.guarantee.heading?.trim() || `${landing.guarantee.days || 30}-Day Money-Back Guarantee`}</h3>
                            <div className="mt-4 text-[15px] leading-relaxed text-stone-600 [&_p]:mb-2.5" dangerouslySetInnerHTML={{ __html: landing.guarantee.description?.trim() || guaranteeDefaultHtml(landing.guarantee.days || 30) }} />
                        </div>
                    </div>
                </div>
            </Reveal>
        ),
        faq: landing.faq?.show && landing.faq.list?.length > 0 && (
            <Reveal>
                <div id="at-faq" className="scroll-mt-24">
                    <Sec n={5} eyebrow="FAQ" title={landing.faq.heading || "Frequently asked questions"} A={A} AD={AD}>
                        <div className="mt-8 border-t" style={{ borderColor: rgba(INK, 0.12) }}>
                            {landing.faq.list.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} A={A} />)}
                        </div>
                    </Sec>
                </div>
            </Reveal>
        ),
    };

    return (
        <div className={embedded ? "bg-white" : "min-h-screen"} style={{ fontFamily: BODYFONT, background: embedded ? "#fff" : PAPER, color: INK }}>
            <style>{`
                html{scroll-behavior:smooth}
                @keyframes atFade{from{opacity:0}to{opacity:1}}
            `}</style>

            {/* header */}
            {!embedded && (
                <header className="sticky top-0 z-30 border-b bg-[#fbfaf7]/85 backdrop-blur-md" style={{ borderColor: rgba(INK, 0.1) }}>
                    <div className="relative mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
                        <div className="flex items-center gap-3">
                            {community.logo && String(community.logo).trim()
                                ? <img src={community.logo} alt={community.name} className="h-8 w-auto max-w-[150px] object-contain" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = `${process.env.PUBLIC_URL || ""}/images/flexicommunity-logo.png`; }} />
                                : <img src={`${process.env.PUBLIC_URL || ""}/images/flexicommunity-logo.png`} alt="FlexiCommunity" className="h-8 w-auto max-w-[170px] object-contain" />}
                            {member && myGroups.length > 0 && <GroupSwitcher groups={myGroups} currentToken={gid} primary={A} onSelect={onSwitchGroup} onDiscover={onDiscoverGroups} />}
                        </div>
                        {navLinks.length > 0 && (
                            <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-10 lg:flex">
                                {navLinks.map((l) => (
                                    <a key={l.href} href={l.href} className="group relative py-1 text-[12px] font-semibold uppercase tracking-[0.14em] text-stone-500 transition-colors duration-200 hover:text-stone-900">
                                        {l.label}
                                        <span className="absolute -bottom-0.5 left-0 h-px w-0 transition-all duration-300 ease-out group-hover:w-full" style={{ background: A }} />
                                    </a>
                                ))}
                            </nav>
                        )}
                        {member ? (
                            <button onClick={() => onJoin(group)} style={{ background: A }} className="rounded-full px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.12em] text-white transition hover:-translate-y-0.5">{joinLabel}</button>
                        ) : isPaid ? (
                            <button onClick={() => onJoin(group)} style={{ background: A }} className="rounded-full px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.12em] text-white transition hover:-translate-y-0.5">Get Access</button>
                        ) : (
                            <button onClick={() => onLogin(group)} style={{ background: A }} className="rounded-full px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.12em] text-white transition hover:-translate-y-0.5">Log In</button>
                        )}
                    </div>
                </header>
            )}

            <main className="relative mx-auto max-w-6xl px-5 pb-20 pt-10 sm:pt-14">
                {/* editorial hero: headline + meta + CTA | framed media */}
                <section className="grid items-center gap-10 lg:grid-cols-[1.05fr_1fr] lg:gap-14">
                    <div>
                        <span className="text-[0.72rem] font-bold uppercase tracking-[0.28em]" style={{ color: B }}>The Community</span>
                        <h1 className="mt-5 text-[2.6rem] leading-[1.05] text-stone-900 sm:text-[3.6rem]" style={{ fontFamily: SERIF, fontWeight: 800 }}>
                            {t.before}{t.accent && <> <span style={{ color: A, fontStyle: "italic" }}>{t.accent}</span></>}{t.after ? ` ${t.after}` : ""}
                        </h1>
                        {sub && <p className="mt-6 max-w-xl text-[17px] leading-[1.8] text-stone-600">{sub}</p>}
                        <span className="mt-7 block h-px w-full max-w-md" style={{ background: rgba(INK, 0.14) }} />
                        <div className="mt-6">{chips}</div>
                        <div className="mt-8 flex flex-wrap items-center gap-5">
                            <JoinButton />
                            <span className="text-sm font-medium text-stone-500">Join {memberCount > 0 ? `${memberCount}+ members` : "the community"}</span>
                        </div>
                    </div>

                    {/* framed media */}
                    <Reveal>
                        {mediaShow ? (
                            <Carousel media={media} defaultImage={defaultImage} A={A} B={B} title={group.name} />
                        ) : (
                            <div className="relative">
                                <span className="absolute -left-3 -top-3 h-full w-full border" style={{ borderColor: rgba(B, 0.5) }} />
                                <div className="relative aspect-[4/3] w-full overflow-hidden bg-white" style={{ boxShadow: `0 30px 70px -34px ${rgba(INK, 0.45)}` }}>
                                    <img src={group.banner || defaultImage} alt={group.name} className="h-full w-full object-cover" onError={(e) => { if (e.currentTarget.src !== defaultImage) e.currentTarget.src = defaultImage; }} />
                                </div>
                            </div>
                        )}
                    </Reveal>
                </section>

                {/* stats strip — editorial ledger */}
                <div className="mt-16 grid grid-cols-3 border-y" style={{ borderColor: rgba(INK, 0.14) }}>
                    {stats.map(([lbl, val], i) => (
                        <div key={lbl} className={`px-4 py-6 text-center ${i > 0 ? "border-l" : ""}`} style={{ borderColor: rgba(INK, 0.14) }}>
                            <div className="text-[2rem] leading-none text-stone-900 sm:text-[2.6rem]" style={{ fontFamily: SERIF, fontWeight: 700 }}>{Number(val) || 0}</div>
                            <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.2em] text-stone-400">{lbl}</div>
                        </div>
                    ))}
                </div>

                {/* content sections */}
                <div className="mt-20 space-y-20">
                    {order.map((id) => <Fragment key={id}>{blocks[id]}</Fragment>)}
                </div>

                {/* join CTA panel */}
                <Reveal>
                    <div className="relative mt-20 overflow-hidden border p-10 text-center sm:p-14" style={{ background: "#fff", borderColor: rgba(INK, 0.12), boxShadow: `0 30px 70px -44px ${rgba(INK, 0.4)}` }}>
                        <span className="pointer-events-none absolute inset-x-0 top-0 h-1" style={{ background: `linear-gradient(90deg, ${A}, ${B})` }} />
                        <div className="mx-auto max-w-xl">
                            <span className="text-[0.72rem] font-bold uppercase tracking-[0.24em]" style={{ color: B }}>Become a member</span>
                            <h3 className="mt-3 text-[2rem] leading-tight text-stone-900 sm:text-[2.6rem]" style={{ fontFamily: SERIF, fontWeight: 700 }}>{group.name}</h3>
                            {shortDescription && <p className="mx-auto mt-4 max-w-md text-[15.5px] leading-relaxed text-stone-600">{shortDescription}</p>}
                            <div className="mx-auto mt-8 max-w-xs">
                                {referral && group.joined
                                    ? <ReferralLink token={token} groupToken={gid} urlToken={referral.urlToken} primary={A} points={referral.points} />
                                    : <JoinButton full />}
                            </div>
                            {community?.show_branding && <PoweredByFlexiFunnels className="mt-6" />}
                        </div>
                    </div>
                </Reveal>
            </main>

            {/* footer */}
            {(
                <footer className="relative border-t" style={{ borderColor: rgba(INK, 0.12) }}>
                    <div className="mx-auto max-w-6xl px-5 py-7">
                        <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:items-center sm:justify-between sm:text-left">
                            <div className="flex items-center gap-3">
                                {community.logo && String(community.logo).trim()
                                    ? <img src={community.logo} alt={community.name} className="h-9 w-auto max-w-[150px] object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                    : <span className="flex h-10 w-10 items-center justify-center rounded-full text-white" style={{ background: A }}><Icon d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" className="h-5 w-5" stroke={2} /></span>}
                                <div>
                                    <p className="text-[15px] text-stone-900" style={{ fontFamily: SERIF, fontWeight: 700 }}>{community.name}</p>
                                    <p className="text-[13px] text-stone-400">{group.name}</p>
                                </div>
                            </div>

                            <div className="flex flex-col items-center gap-4 sm:flex-row sm:gap-6">
                                {socialLinks.length > 0 && (
                                    <div className="flex items-center gap-2.5">
                                        {socialLinks.map(([k, d]) => (
                                            <a key={k} href={social[k]} target="_blank" rel="noopener noreferrer" aria-label={k}
                                                className="flex h-10 w-10 items-center justify-center rounded-full border text-stone-500 transition-all duration-200 hover:-translate-y-1 hover:text-white" style={{ borderColor: rgba(INK, 0.15) }}
                                                onMouseEnter={(e) => { e.currentTarget.style.background = A; e.currentTarget.style.borderColor = "transparent"; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.background = ""; e.currentTarget.style.borderColor = rgba(INK, 0.15); }}>
                                                <svg viewBox="0 0 24 24" fill="currentColor" className="h-[18px] w-[18px]"><path d={d} /></svg>
                                            </a>
                                        ))}
                                    </div>
                                )}
                                <PolicyLinks policies={landing.policies} accent={A} className="text-stone-500" />
                            </div>
                        </div>
                    </div>
                </footer>
            )}
        </div>
    );
}

// Editorial framed carousel — auto-advances, pauses on hover and on video slides.
function Carousel({ media, defaultImage, A, B, title }) {
    const [idx, setIdx] = useState(0);
    const [hover, setHover] = useState(false);
    const n = media.length;
    const cur = media[idx] || {};
    const isVideo = cur.type === "video";
    useEffect(() => {
        if (n <= 1 || hover || isVideo) return undefined;
        const timer = setInterval(() => setIdx((i) => (i + 1) % n), 4800);
        return () => clearInterval(timer);
    }, [n, hover, isVideo]);
    const go = (d) => setIdx((i) => (i + d + n) % n);
    return (
        <div className="relative">
            <span className="absolute -left-3 -top-3 h-full w-full border" style={{ borderColor: rgba(B, 0.5) }} />
            <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} className="group relative aspect-[4/3] w-full overflow-hidden bg-stone-100" style={{ boxShadow: `0 30px 70px -34px ${rgba(A, 0.4)}` }}>
                {cur.type === "video"
                    ? <iframe key={idx} src={videoEmbedUrl(cur.platform, cur.url)} title={title} className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                    : <img key={idx} src={cur.image || defaultImage} alt={title} className="h-full w-full animate-[atFade_.6s_ease] object-cover" onError={(e) => { if (e.currentTarget.src !== defaultImage) e.currentTarget.src = defaultImage; }} />}
                {n > 1 && (
                    <>
                        <button type="button" onClick={() => go(-1)} aria-label="Previous" className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-stone-800 shadow-lg backdrop-blur transition hover:bg-white sm:opacity-0 sm:group-hover:opacity-100"><Icon d="M15.75 19.5L8.25 12l7.5-7.5" className="h-5 w-5" stroke={2.4} /></button>
                        <button type="button" onClick={() => go(1)} aria-label="Next" className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-stone-800 shadow-lg backdrop-blur transition hover:bg-white sm:opacity-0 sm:group-hover:opacity-100"><Icon d="M8.25 4.5l7.5 7.5-7.5 7.5" className="h-5 w-5" stroke={2.4} /></button>
                        <div className="absolute inset-x-0 bottom-4 flex items-center justify-center gap-2">
                            {media.map((_, i) => <button key={i} type="button" onClick={() => setIdx(i)} aria-label={`Slide ${i + 1}`} className="h-1.5 rounded-full transition-all" style={{ width: i === idx ? 26 : 8, background: i === idx ? "#fff" : "rgba(255,255,255,.6)" }} />)}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// Section wrapper — thin top rule, small index number, small-caps eyebrow, serif title.
function Sec({ id, n, eyebrow, title, sub, A, AD, children }) {
    return (
        <section id={id} className="scroll-mt-24">
            <div className="border-t pt-6" style={{ borderColor: rgba(INK, 0.16) }}>
                <div className="flex items-baseline gap-4">
                    <span className="text-[13px] font-semibold tabular-nums" style={{ color: A }}>{String(n).padStart(2, "0")}</span>
                    <span className="text-[0.72rem] font-bold uppercase tracking-[0.24em]" style={{ color: AD }}>{eyebrow}</span>
                </div>
                <h2 className="mt-3 text-[2rem] leading-tight text-stone-900 sm:text-[2.6rem]" style={{ fontFamily: SERIF, fontWeight: 700 }}>{title}</h2>
                {sub && <div className="mt-3 max-w-2xl text-[15.5px] leading-relaxed text-stone-500" dangerouslySetInnerHTML={{ __html: sub }} />}
            </div>
            {children}
        </section>
    );
}

function FaqItem({ q, a, A }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b" style={{ borderColor: rgba(INK, 0.12) }}>
            <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-4 py-5 text-left">
                <span className="text-[16px] font-semibold text-stone-900" style={{ fontFamily: SERIF }}>{q}</span>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-transform duration-300" style={{ borderColor: rgba(A, 0.4), color: A, transform: open ? "rotate(45deg)" : "none" }}><Icon d="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" stroke={2.2} /></span>
            </button>
            <div className="grid transition-all duration-300" style={{ gridTemplateRows: open ? "1fr" : "0fr" }}>
                <div className="overflow-hidden"><p className="pb-6 pr-10 text-[15px] leading-relaxed text-stone-600">{a}</p></div>
            </div>
        </div>
    );
}
